import { useState, useEffect, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
// import { updateDistrictTitle } from 'src/shared/services/districtService';
import { Box } from '@mui/material';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { OrganizationTitleEditDate } from 'src/shared/components/organisms/organizationTitleEditDate';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { OrganizationTitleRequest } from 'src/shared/models/organization/organizationTitleRequest';
import { OrganizationTitleResponse } from 'src/shared/models/organization/organizationTitleResponse';
import { AxiosResponse } from 'axios';

type OrganizationTitleEditDateModalProps = {
  modalOpen: boolean;
  modalTitle: string;
  organizationTitleResponse: OrganizationTitleResponse;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateMethod: (data: OrganizationTitleRequest) => Promise<AxiosResponse<any>>; // TODO: Typa
  onSave: () => void;
  onClose: () => void;
};

export const OrganizationTitleEditDateModal = (props: OrganizationTitleEditDateModalProps) => {
  const { modalOpen, modalTitle, organizationTitleResponse, updateMethod, onSave, onClose } = props;
  const { t } = useTranslation(['common', 'district']);
  const [loading, setLoading] = useState<boolean>(false);

  const isMounted = useIsMounted();

  // const requiredMessage = t('common:validationMessages.required');

  const OrganizationTitleRequestSchema: Schema<OrganizationTitleRequest> = yup.object().shape({
    memberTitle_id: yup.number().required(),
    member_id: yup.number().required(),
    admDate: yup.string().nullable().optional(),
    resDate: yup.string().nullable().optional(),
  });

  const formMethods = useForm<OrganizationTitleRequest>({
    resolver: yupResolver(OrganizationTitleRequestSchema),
    mode: 'all',
    defaultValues: {
      memberTitle_id: 0,
      member_id: 0,
      admDate: null,
      resDate: null,
    },
  });

  const { handleSubmit, reset, trigger } = formMethods;

  useEffect(() => {
    ConsoleHelper.log('organizationTitleResponse', organizationTitleResponse);
    const resetObject: OrganizationTitleRequest = {
      memberTitle_id: organizationTitleResponse.memberTitle_id,
      member_id: organizationTitleResponse.member_id || 0,
      resDate: organizationTitleResponse.resDate || null,
      admDate: organizationTitleResponse.admDate || null,
    };
    reset(resetObject);
  }, [organizationTitleResponse, isMounted, reset]);

  const saveFormData = useCallback(
    async (data: OrganizationTitleRequest) => {
      setLoading(true);
      try {
        const requestData: OrganizationTitleRequest = {
          memberTitle_id: data.memberTitle_id,
          member_id: data.member_id,
          admDate: data.admDate ? DateHelper.getFSDate(data.admDate) : '',
          resDate: data.resDate ? DateHelper.getFSDate(data.resDate) : '',
        };
        await updateMethod(requestData);
        if (isMounted()) {
          toast.success(<>{t('district:titleConfigModal.saveSuccess')}</>);
          onSave();
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('saveFormDataError:', e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [isMounted, onSave, t, updateMethod],
  );

  const onSubmit = useCallback(
    async (data: OrganizationTitleRequest) => {
      ConsoleHelper.log('onSubmit', data);
      await trigger();
      await saveFormData(data);
    },
    [saveFormData, trigger],
  );

  const onHandleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const onHandleSave = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      dialogTitle={modalTitle}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogContent={
        <FormLoaderWrapper loading={loading}>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ p: 3 }}>
                <OrganizationTitleEditDate />
              </Box>
            </form>
          </FormProvider>
        </FormLoaderWrapper>
      }
    />
  );
};
